/// <reference path="ShellProtocol.ts" />

interface ClusterFrame
{
    element:HTMLIFrameElement;
    origin:string;
    ready:boolean;
}

interface ClusterOpenRequest
{
    item:any;
    origin:string;
    target:any;
    completion:any;
    timeout:number;
}

/**
 * Shell side of the handoff (AppMode = shell): loads the manager of the cluster a
 * business lives on inside an iframe and hands the session over.
 *
 * AppDelegate.setBusiness branches here, so the login screen and the workspace
 * picker keep calling what they call today; their completion runs once the cluster
 * manager has acknowledged the session, which is when the login modal zooms out and
 * reveals the manager already on its dashboard.
 */
class ClusterSessionController
{
    private static _sharedInstance:ClusterSessionController = null;
    static sharedInstance():ClusterSessionController {
        if (ClusterSessionController._sharedInstance == null) {
            ClusterSessionController._sharedInstance = new ClusterSessionController();
        }
        return ClusterSessionController._sharedInstance;
    }

    private static readonly AckTimeout = 20000;

    private current:ClusterFrame = null;   // the manager on screen
    private pending:ClusterFrame = null;   // another cluster loading behind it
    private request:ClusterOpenRequest = null;
    private currentItem:any = null;
    private listening = false;

    openBusiness(item:any, target?:any, completion?:any) {
        let url = this.managerURL(item);
        if (url == null) {
            MIOLog("ClusterSessionController: no manager URL for business " + item["id"]);
            this.reportFailure(target, completion);
            return;
        }

        this.ensureListening();
        this.cancelRequest();

        let origin = ShellOriginOfURL(url);
        this.request = {
            item: item,
            origin: origin,
            target: target,
            completion: completion,
            timeout: window.setTimeout(() => this.requestFailed("timeout"), ClusterSessionController.AckTimeout)
        };

        let frame = this.frameForOrigin(origin);
        if (frame != null) {
            if (frame.ready) this.postSession(frame, item);
            // Otherwise the frame is still loading and will announce itself.
            return;
        }

        // Another cluster: load it behind the current manager, which stays until the
        // new one acknowledges the session.
        this.removeFrame(this.pending);
        this.pending = this.createFrame(url, origin);
    }

    private managerURL(item:any):string {
        let settings = SettingsHelper.sharedInstance();
        let base = settings.clusterManagerURL;
        if (base == null) {
            let urls = ShellBusinessURLs(item);
            base = urls["ManagerURL"] ?? ShellOriginOfURL(urls["APIServerURL"]);
        }
        if (base == null) return null;
        if (base.endsWith("/") == false) base += "/";
        return base + "?embedded=1";
    }

    private ensureListening() {
        if (this.listening) return;
        this.listening = true;
        window.addEventListener("message", (ev:MessageEvent) => this.messageReceived(ev));
    }

    private frameForOrigin(origin:string):ClusterFrame {
        if (this.current != null && this.current.origin == origin) return this.current;
        if (this.pending != null && this.pending.origin == origin) return this.pending;
        return null;
    }

    private frameForSource(source:any):ClusterFrame {
        if (this.current != null && this.current.element.contentWindow === source) return this.current;
        if (this.pending != null && this.pending.element.contentWindow === source) return this.pending;
        return null;
    }

    private createFrame(url:string, origin:string):ClusterFrame {
        let element = document.createElement("iframe");
        element.className = "cluster-frame";
        element.setAttribute("allow", "microphone; camera; clipboard-read; clipboard-write; fullscreen");
        element.setAttribute("allowfullscreen", "true");
        element.style.visibility = "hidden";

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let container = (ad.window.rootViewController as MainViewController).contentView;
        container.layer.appendChild(element);

        // The listener is attached before the document starts loading.
        element.src = url;

        return { element: element, origin: origin, ready: false };
    }

    private removeFrame(frame:ClusterFrame) {
        if (frame == null) return;
        if (frame.element.parentNode != null) frame.element.parentNode.removeChild(frame.element);
        if (frame === this.current) this.current = null;
        if (frame === this.pending) this.pending = null;
    }

    private messageReceived(ev:MessageEvent) {
        let frame = this.frameForSource(ev.source);
        if (frame == null || ev.origin != frame.origin) return;

        let message = ev.data;
        if (message == null || typeof message != "object") return;

        switch (message["type"]) {
            case ShellMessage.ManagerReady:
                frame.ready = true;
                if (this.request != null && this.request.origin == frame.origin) this.postSession(frame, this.request.item);
                break;

            case ShellMessage.SessionAck:
                this.sessionAcknowledged(frame);
                break;

            case ShellMessage.SessionError:
                this.requestFailed(message["reason"]);
                break;

            case ShellMessage.ShowWorkspace:
                this.showWorkspace();
                break;

            case ShellMessage.SessionExpired:
                this.sessionExpired();
                break;
        }
    }

    private postSession(frame:ClusterFrame, item:any) {
        let auth = DualLinkAuthKit.sharedInstance();
        let session:ShellSession = {
            type: ShellMessage.Session,
            accessToken: auth.token,
            user: { id: auth.userID, email: auth.userEmail, name: auth.userName },
            business: item
        };
        frame.element.contentWindow.postMessage(session, frame.origin);
    }

    private sessionAcknowledged(frame:ClusterFrame) {
        if (frame === this.pending) {
            this.removeFrame(this.current);
            this.current = frame;
            this.pending = null;
        }
        frame.element.style.visibility = "visible";

        let request = this.request;
        if (request == null) return;
        this.request = null;
        window.clearTimeout(request.timeout);

        this.currentItem = request.item;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.saveLastWorkspace(request.item);

        if (request.target != null && request.completion != null) request.completion.call(request.target);
    }

    private cancelRequest() {
        if (this.request == null) return;
        window.clearTimeout(this.request.timeout);
        this.request = null;
    }

    private requestFailed(reason:string) {
        let request = this.request;
        if (request == null) return;
        this.request = null;
        window.clearTimeout(request.timeout);

        MIOLog("ClusterSessionController: could not open business " + request.item["id"] + " (" + reason + ")");

        // A cluster that never answered is dropped so the next attempt loads it again.
        if (this.pending != null && this.pending.origin == request.origin) this.removeFrame(this.pending);
        if (this.current != null && this.current.origin == request.origin && this.current.ready == false) this.removeFrame(this.current);

        this.reportFailure(request.target, request.completion);
    }

    private reportFailure(target:any, completion:any) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let parent = target instanceof MUIViewController ? target : ad.window.rootViewController;
        AppHelper.showErrorMessage(parent, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_WORKSPACE_COULD_NOT_BE_OPENED", "The workspace could not be opened. Please try again."));
    }

    private showWorkspace() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        AppHelper.sharedInstance().presentWorkspaceViewController(ad.window.rootViewController);
    }

    /** The cluster manager got a 401: renew the session here, fall back to the login. */
    private sessionExpired() {
        if (this.currentItem == null) {
            this.showLogin();
            return;
        }

        DualLinkAuthKit.sharedInstance().refreshSession((error:boolean, result:boolean) => {
            if (result == true) this.openBusiness(this.currentItem);
            else this.showLogin();
        });
    }

    private showLogin() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.loginOK = false;
        (ad.window.rootViewController as MainViewController).showLoginViewController();
    }
}
